import { motion } from "framer-motion";
import { Sparkles, RefreshCw, Loader2, Pin, Copy, Check, X } from "lucide-react";

export const TYPE_COLORS = {
  Deeper: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10",
  Clarify: "text-sky-300 border-sky-500/30 bg-sky-500/10",
  Challenge: "text-rose-300 border-rose-500/30 bg-rose-500/10",
  Story: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  Connect: "text-violet-300 border-violet-500/30 bg-violet-500/10",
  Pivot: "text-slate-200 border-slate-500/30 bg-slate-500/10",
};

export const QuestionCard = ({ q, index, pinned, onPin, onCopy, onUsed, stale }) => (
  <motion.article initial={{ opacity: 0, x: 12 }} animate={{ opacity: stale ? 0.45 : 1, x: 0 }} transition={{ delay: index * 0.04 }}
    className="group iv-panel-inner p-4 hover:border-white/25 transition-colors duration-200" data-testid="question-card">
    <div className="flex items-center justify-between mb-2">
      <span className={`rounded border px-2 py-0.5 text-[0.72rem] font-mono uppercase tracking-widest ${TYPE_COLORS[q.type] || TYPE_COLORS.Deeper}`} data-testid="question-type-badge">{q.type}</span>
      <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
        <button onClick={onUsed} title="I asked this" data-testid="mark-asked-button" className="iv-icon-btn"><Check size={15} /></button>
        <button onClick={onCopy} title="Copy" data-testid="copy-question-button" className="iv-icon-btn"><Copy size={15} /></button>
        <button onClick={onPin} title="Pin" data-testid="pin-question-button" className={`iv-icon-btn ${pinned ? "text-amber-400" : ""}`}><Pin size={15} fill={pinned ? "currentColor" : "none"} /></button>
      </div>
    </div>
    <p className="text-lg md:text-xl font-medium leading-snug text-white" data-testid="question-text">{q.text}</p>
  </motion.article>
);

const FOCUSES = [["balanced", "Balanced"], ["stories", "Stories"], ["technical", "Specifics"], ["challenge", "Challenge"], ["timeline", "Timeline"]];

export const SuggestionsPanel = ({ questions, loading, error, focus, setFocus, onRefresh, isPinned, onPin, onCopy, onUsed, canGenerate, auto, setAuto }) => (
  <section className="iv-panel flex flex-col" data-testid="ai-suggestions-panel">
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-white/10">
      <span className="eyebrow flex items-center gap-2">
        <Sparkles size={13} /> Ask next
        {loading && <Loader2 size={13} className="animate-spin text-sky-400" data-testid="suggestions-loading" />}
      </span>
      <div className="flex items-center gap-2">
        <button onClick={() => setAuto(!auto)} data-testid="auto-toggle-button"
          className={`text-[0.72rem] font-mono uppercase tracking-widest rounded-full border px-2.5 py-1 transition-colors ${auto ? "border-sky-400/40 text-sky-300" : "border-white/10 text-slate-300"}`}>
          Auto {auto ? "on" : "off"}
        </button>
        <button onClick={onRefresh} disabled={!canGenerate} data-testid="generate-now-button"
          className="flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-40 px-3 py-1 text-xs transition-colors">
          <RefreshCw size={12} /> Generate now
        </button>
      </div>
    </div>
    <div className="flex gap-1.5 overflow-x-auto px-4 pt-3" data-testid="focus-filters">
      {FOCUSES.map(([k, label]) => (
        <button key={k} onClick={() => setFocus(k)} data-testid={`focus-${k}-button`}
          className={`shrink-0 rounded-full px-3 py-1 text-xs transition-colors ${focus === k ? "bg-sky-400 text-[#070F1E] font-semibold" : "bg-white/5 text-slate-300 hover:text-white"}`}>
          {label}
        </button>
      ))}
    </div>
    <div className="p-4 space-y-3">
      {error && <p className="text-sm text-rose-400" data-testid="suggestions-error">{error}</p>}
      {questions.length === 0 && !loading && (
        <p className="text-sm text-slate-300 py-10 leading-relaxed" data-testid="suggestions-empty">
          Follow-up questions appear here automatically each time your guest pauses. They keep updating while the guest talks, so a fresh set is ready the moment they finish.
        </p>
      )}
      {questions.map((q, i) => (
        <QuestionCard key={`${q.text}-${i}`} q={q} index={i} stale={loading && q.stale} pinned={isPinned(q.text)}
          onPin={() => onPin(q)} onCopy={() => onCopy(q.text)} onUsed={() => onUsed(q)} />
      ))}
    </div>
  </section>
);

export const PinnedPanel = ({ pinned, onToggleAsked, onRemove, onCopy, onClear }) => (
  <section className="iv-panel" data-testid="pinned-questions-panel">
    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
      <span className="eyebrow flex items-center gap-2"><Pin size={13} className="text-amber-400" /> Shortlist <span className="text-slate-300">{pinned.length}</span></span>
      {pinned.length > 0 && <button onClick={onClear} data-testid="clear-pinned-button" className="text-[0.72rem] font-mono uppercase tracking-widest text-slate-300 hover:text-rose-400 transition-colors">Clear</button>}
    </div>
    <ul className="p-3 space-y-1.5">
      {pinned.length === 0 && <li className="px-1 py-3 text-sm text-slate-300" data-testid="pinned-empty">Pin a question to keep it handy for later.</li>}
      {pinned.map((p) => (
        <li key={p.id} data-testid="pinned-question-item" className="group flex items-start gap-2 rounded-md px-2 py-2 hover:bg-white/5 transition-colors">
          <button onClick={() => onToggleAsked(p.id)} data-testid="pinned-asked-toggle"
            className={`mt-0.5 h-4 w-4 shrink-0 rounded border grid place-items-center transition-colors ${p.asked ? "bg-amber-500 border-amber-500 text-black" : "border-slate-600 hover:border-amber-400"}`}>
            {p.asked && <Check size={11} strokeWidth={3} />}
          </button>
          <p className={`flex-1 text-sm leading-snug ${p.asked ? "line-through text-slate-400" : "text-white"}`}>{p.text}</p>
          <button onClick={() => onCopy(p.text)} data-testid="pinned-copy-button" className="iv-icon-btn opacity-0 group-hover:opacity-100"><Copy size={13} /></button>
          <button onClick={() => onRemove(p.id)} data-testid="pinned-remove-button" className="iv-icon-btn opacity-0 group-hover:opacity-100"><X size={13} /></button>
        </li>
      ))}
    </ul>
  </section>
);
